import React, { useState } from "react";

import { Field, Label, Description, ErrorMessage } from "V2.0/common/fieldset";
import { Input } from "V2.0/common/input";
import { Select } from "V2.0/common/select";
import { Button } from "V2.0/common/button";
import { Badge } from "V2.0/common/badge";
import { Dialog, DialogTitle, DialogDescription, DialogBody, DialogActions } from "V2.0/common/dialog";
import { Table, TableHead, TableBody, TableRow, TableHeader, TableCell } from "V2.0/common/table";
import { PlusIcon, PencilIcon, TrashIcon } from "@heroicons/react/16/solid";

import { ResponseField, SAVE_TO_OPTIONS, READ_AS_OPTIONS, ReadAs, SaveTo, newResponseField, isLegacyUnsaved } from "../models/responseField";

type Props = {
  responseFields: ResponseField[];
  onFieldsChange: (fields: ResponseField[]) => void;
};

export function SaveFromResponse({ responseFields, onFieldsChange }: Props) {
  const [draft, setDraft] = useState<ResponseField | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const readAsLabel = (value: string) => READ_AS_OPTIONS.find((option) => option.value === value)?.label ?? value;
  const saveToLabel = (value: string) => SAVE_TO_OPTIONS.find((option) => option.value === value)?.label ?? value;

  const cancel = () => {
    setDraft(null);
    setEditingIndex(null);
  };
  const commit = (field: ResponseField) => {
    const next = [...responseFields];
    if (editingIndex === null) next.push(field);
    else next[editingIndex] = field;
    onFieldsChange(next);
    cancel();
  };
  const remove = (index: number) => {
    onFieldsChange(responseFields.filter((_, i) => i !== index));
    if (editingIndex === index) cancel();
  };

  const hasLegacyRows = responseFields.some((field) => isLegacyUnsaved(field.raw));

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-end justify-between gap-4">
        <p className="text-base/6 text-zinc-500 sm:text-sm/6">
          Values saved for the assistant can be used in the prompt and in spoken messages with curly brackets, for example {"{responseMessage}"}.
        </p>
        <Button
          outline
          className="shrink-0"
          onClick={() => {
            setDraft(newResponseField());
            setEditingIndex(null);
          }}
        >
          <PlusIcon />
          Add a response field
        </Button>
      </div>

      {responseFields.length > 0 ? (
        <div className="rounded-lg border border-zinc-200 px-4">
          <Table dense>
            <TableHead>
              <TableRow>
                <TableHeader>Response field</TableHeader>
                <TableHeader>Read as</TableHeader>
                <TableHeader>Save to</TableHeader>
                <TableHeader className="w-px" />
              </TableRow>
            </TableHead>
            <TableBody>
              {responseFields.map((field, index) => (
                <TableRow key={`${field.name}-${index}`}>
                  <TableCell className="font-medium">
                    <span className="flex items-center gap-2">
                      {field.name}
                      {isLegacyUnsaved(field.raw) && <Badge color="amber">Was not being saved</Badge>}
                    </span>
                  </TableCell>
                  <TableCell>
                    {readAsLabel(field.readAs)}
                    {field.readAs === "jsonField" && field.path && <span className="ml-1 text-zinc-500">{field.path}</span>}
                  </TableCell>
                  <TableCell>
                    {saveToLabel(field.saveTo)}
                    {field.saveTo === "assistant" && field.name && <span className="ml-1 text-zinc-500">{`{${field.name}}`}</span>}
                  </TableCell>
                  <TableCell>
                    <div className="flex justify-end gap-1">
                      <Button
                        plain
                        aria-label={`Edit ${field.name}`}
                        onClick={() => {
                          setDraft({ ...field });
                          setEditingIndex(index);
                        }}
                      >
                        <PencilIcon />
                      </Button>
                      <Button plain aria-label={`Remove ${field.name}`} onClick={() => remove(index)}>
                        <TrashIcon />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <p className="rounded-lg border border-dashed border-zinc-200 px-4 py-6 text-center text-sm text-zinc-500">Nothing is kept from the reply yet.</p>
      )}

      {hasLegacyRows && (
        <p className="text-base/6 text-amber-700 sm:text-sm/6">
          Rows marked "Was not being saved" were set up without anywhere to keep the value, so nothing used them. Saving this API starts saving
          them where the row says.
        </p>
      )}

      <ResponseFieldDialog draft={draft} isNew={editingIndex === null} onChange={setDraft} onCancel={cancel} onCommit={commit} />
    </div>
  );
}

type DialogProps = {
  draft: ResponseField | null;
  isNew: boolean;
  onChange: (draft: ResponseField) => void;
  onCancel: () => void;
  onCommit: (field: ResponseField) => void;
};

function ResponseFieldDialog({ draft, isNew, onChange, onCancel, onCommit }: DialogProps) {
  const [error, setError] = useState<string | null>(null);
  const name = (draft?.name ?? "").trim();

  const close = () => {
    setError(null);
    onCancel();
  };

  const submit = () => {
    if (!draft) return;
    if (!name) {
      setError("Give the response field a name.");
      return;
    }
    setError(null);
    onCommit({ ...draft, name });
  };

  const saveToDescription = (saveTo: SaveTo) => {
    const option = SAVE_TO_OPTIONS.find((item) => item.value === saveTo);
    if (!option) return "";
    return saveTo === "assistant" ? `${option.description} For this field: {${name || "responseMessage"}}` : option.description;
  };

  return (
    <Dialog open={draft !== null} onClose={close} size="lg">
      {draft && (
        <>
          <DialogTitle>{isNew ? "Add a response field" : "Edit response field"}</DialogTitle>
          <DialogDescription>Kept from the reply once the request succeeds.</DialogDescription>
          <DialogBody className="flex flex-col gap-6">
            <Field>
              <Label>Response field</Label>
              <Input
                name="responseFieldName"
                value={draft.name}
                onChange={(event) => {
                  setError(null);
                  onChange({ ...draft, name: event.target.value });
                }}
                invalid={!!error}
              />
              <Description>The name the value is saved under.</Description>
              {error && <ErrorMessage>{error}</ErrorMessage>}
            </Field>

            <Field>
              <Label>Read as</Label>
              <Select name="responseFieldReadAs" value={draft.readAs} onChange={(event) => onChange({ ...draft, readAs: event.target.value as ReadAs })}>
                {READ_AS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
            </Field>

            {draft.readAs === "jsonField" && (
              <Field>
                <Label>Which field</Label>
                <Input name="responseFieldPath" value={draft.path} onChange={(event) => onChange({ ...draft, path: event.target.value })} placeholder="order.confirmationCode" />
                <Description>The path inside the JSON reply. Leave empty to use the response field name.</Description>
              </Field>
            )}

            <Field>
              <Label>Save to</Label>
              <Select name="responseFieldSaveTo" value={draft.saveTo} onChange={(event) => onChange({ ...draft, saveTo: event.target.value as SaveTo })}>
                {SAVE_TO_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
              <Description>{saveToDescription(draft.saveTo)}</Description>
            </Field>
          </DialogBody>
          <DialogActions>
            <Button plain onClick={close}>
              Cancel
            </Button>
            <Button onClick={submit}>{isNew ? "Add field" : "Save field"}</Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
